import type { BackendModuleKey } from "./backendModuleKeys";
import { BACKEND_MODULE_KEYS } from "./backendModuleKeys";
import type { ModuleAccessKey } from "./accessRequests";
import type { ModuleCatalog } from "./moduleCatalog";
import { getCatalogEntry, normalizeCatalogModuleKey } from "./moduleCatalog";

export type RouteGatePlaceholder =
  | "dashboard-card"
  | "page-documents"
  | "page-ai-chat"
  | "page-generic"
  | "settings-rows"
  | "api-table"
  | "inline";

/** Logical gate id (routes, sidebar, settings tabs). Distinct from access-request module when needed. */
export type ModuleGateId =
  | ModuleAccessKey
  | "ai_chat"
  | "family"
  | "finances"
  | "career"
  | "integrations";

export type ModuleGateRow = {
  id: ModuleGateId;
  /** Sent to POST /access-requests/ when requestable; null when no module grant exists. */
  accessRequestModule: BackendModuleKey | null;
  /** REST permission codes that grant access (any one). Overridable via applyBackendModulePermissionMap. */
  permissions: readonly string[];
  requestable: boolean;
  /** Blur overlay + nav semantics title (i18n key). */
  titleI18nKey: string;
  /** Placeholder skeleton variant module icon context. */
  placeholderModule: ModuleAccessKey;
};

/** Maps UI gate ids to catalog module keys (API may use ai-chat). */
export const GATE_CATALOG_MODULE_KEY: Record<ModuleGateId, string | null> = {
  apartments: "apartments",
  payments: "payments",
  budget: "budget",
  documents: "documents",
  tasks: "tasks",
  reminders: "reminders",
  knowledge: "knowledge",
  ai_chat: "ai_chat",
  family: "family",
  finances: null,
  career: null,
  integrations: "integrations",
};

/**
 * Authoritative module → permission map for gates (DEC-005/006).
 * Gate UI permissions stay here; approval grants come from the module catalog (#172).
 */
export const MODULE_GATES: Record<ModuleGateId, ModuleGateRow> = {
  apartments: {
    id: "apartments",
    accessRequestModule: "apartments",
    permissions: ["apartments.view_all"],
    requestable: true,
    titleI18nKey: "accessRequests.modules.apartments",
    placeholderModule: "apartments",
  },
  payments: {
    id: "payments",
    accessRequestModule: "payments",
    permissions: ["payments.view_all"],
    requestable: true,
    titleI18nKey: "accessRequests.modules.payments",
    placeholderModule: "payments",
  },
  budget: {
    id: "budget",
    accessRequestModule: "budget",
    permissions: ["budget.view_full"],
    requestable: true,
    titleI18nKey: "accessRequests.modules.budget",
    placeholderModule: "budget",
  },
  documents: {
    id: "documents",
    accessRequestModule: "documents",
    permissions: ["documents.view_all"],
    requestable: true,
    titleI18nKey: "accessRequests.modules.documents",
    placeholderModule: "documents",
  },
  tasks: {
    id: "tasks",
    accessRequestModule: "tasks",
    permissions: ["tasks.view_all"],
    requestable: true,
    titleI18nKey: "accessRequests.modules.tasks",
    placeholderModule: "tasks",
  },
  reminders: {
    id: "reminders",
    accessRequestModule: "reminders",
    permissions: ["reminders.view"],
    requestable: true,
    titleI18nKey: "accessRequests.modules.reminders",
    placeholderModule: "reminders",
  },
  knowledge: {
    id: "knowledge",
    accessRequestModule: "knowledge",
    permissions: ["knowledge.view_family"],
    requestable: true,
    titleI18nKey: "accessRequests.modules.knowledge",
    placeholderModule: "knowledge",
  },
  ai_chat: {
    id: "ai_chat",
    accessRequestModule: BACKEND_MODULE_KEYS.aiChat,
    permissions: ["ai.view_status"],
    requestable: true,
    titleI18nKey: "aiChat.title",
    placeholderModule: "ai_chat",
  },
  family: {
    id: "family",
    accessRequestModule: "family",
    permissions: ["documents.view_family"],
    requestable: true,
    titleI18nKey: "nav.family",
    placeholderModule: "family",
  },
  finances: {
    id: "finances",
    accessRequestModule: null,
    permissions: ["finances.view_portfolios"],
    requestable: false,
    titleI18nKey: "nav.finances",
    placeholderModule: "payments",
  },
  career: {
    id: "career",
    accessRequestModule: null,
    permissions: ["career.view"],
    requestable: false,
    titleI18nKey: "nav.career",
    placeholderModule: "tasks",
  },
  integrations: {
    id: "integrations",
    accessRequestModule: null,
    permissions: ["integrations.manage"],
    requestable: false,
    titleI18nKey: "settings.tabs.integrations",
    placeholderModule: "knowledge",
  },
};

export type RouteGateBinding = {
  gateId: ModuleGateId;
  match: (normalizedPath: string) => boolean;
  placeholder: RouteGatePlaceholder;
  /** Route-specific permission override (must stay aligned with MODULE_GATES until backend map lands). */
  permissions?: readonly string[];
};

/** Most specific paths first. */
export const ROUTE_GATE_BINDINGS: RouteGateBinding[] = [
  { gateId: "ai_chat", match: (p) => p === "/ai-chat", placeholder: "page-ai-chat" },
  {
    gateId: "documents",
    match: (p) => p.startsWith("/documents") && p !== "/documents/queue",
    placeholder: "page-documents",
  },
  { gateId: "family", match: (p) => p.startsWith("/family"), placeholder: "page-generic" },
  {
    gateId: "apartments",
    match: (p) => p === "/utility-migrations",
    placeholder: "page-generic",
  },
  { gateId: "apartments", match: (p) => p.startsWith("/properties"), placeholder: "page-generic" },
  { gateId: "documents", match: (p) => p.startsWith("/bundles"), placeholder: "page-generic" },
  { gateId: "payments", match: (p) => p.startsWith("/payments"), placeholder: "page-generic" },
  {
    gateId: "budget",
    match: (p) => p === "/estate-planner" || p.startsWith("/budget"),
    placeholder: "page-generic",
  },
  { gateId: "tasks", match: (p) => p.startsWith("/tasks"), placeholder: "page-generic" },
  { gateId: "knowledge", match: (p) => p.startsWith("/knowledge"), placeholder: "page-generic" },
  { gateId: "finances", match: (p) => p.startsWith("/finances"), placeholder: "page-generic" },
  { gateId: "career", match: (p) => p.startsWith("/career"), placeholder: "page-generic" },
];

export const SETTINGS_TAB_GATE_IDS: Record<string, ModuleGateId> = {
  integrations: "integrations",
  apiAccess: "integrations",
};

let backendGatePermissionOverrides: Partial<Record<ModuleGateId, readonly string[]>> | null =
  null;

let catalogRequestableOverrides: Partial<Record<ModuleGateId, boolean>> | null = null;

/** Apply backend-published module→permission map (legacy hook for tests). */
export function applyBackendModulePermissionMap(
  map: Partial<Record<ModuleGateId | ModuleAccessKey, readonly string[]>>,
): void {
  backendGatePermissionOverrides = map as Partial<Record<ModuleGateId, readonly string[]>>;
}

/** Sync requestable flags from GET /access-requests/modules/ (gate permissions unchanged). */
export function applyModuleCatalogToGates(catalog: ModuleCatalog): void {
  const requestable: Partial<Record<ModuleGateId, boolean>> = {};
  for (const gateId of Object.keys(MODULE_GATES) as ModuleGateId[]) {
    const catalogKey = GATE_CATALOG_MODULE_KEY[gateId];
    if (!catalogKey) continue;
    const entry =
      catalog[normalizeCatalogModuleKey(catalogKey)] ?? getCatalogEntry(catalogKey);
    if (entry) {
      requestable[gateId] = entry.requestable;
    }
  }
  catalogRequestableOverrides = requestable;
}

export function gateRequestable(gateId: ModuleGateId): boolean {
  const override = catalogRequestableOverrides?.[gateId];
  if (override !== undefined) {
    return override;
  }
  return MODULE_GATES[gateId].requestable;
}

export function gatePermissionCodes(gateId: ModuleGateId): readonly string[] {
  const override = backendGatePermissionOverrides?.[gateId];
  if (override) {
    return override;
  }
  return MODULE_GATES[gateId].permissions;
}

export function findRouteGateBinding(
  normalizedPath: string,
): RouteGateBinding | undefined {
  return ROUTE_GATE_BINDINGS.find((b) => b.match(normalizedPath));
}

export type ResolvedRouteGate = {
  gateId: ModuleGateId;
  permissions: readonly string[];
  placeholder: RouteGatePlaceholder;
  requestable: boolean;
  titleI18nKey: string;
  accessRequestModule: BackendModuleKey | null;
  placeholderModule: ModuleAccessKey;
};

export function resolveRouteGate(pathname: string): ResolvedRouteGate | null {
  const normalized =
    pathname === "/" ? "/" : pathname.replace(/\/+$/, "") || "/";
  if (normalized === "/" || normalized.startsWith("/settings")) {
    return null;
  }
  const binding = findRouteGateBinding(normalized);
  if (!binding) {
    return null;
  }
  const row = MODULE_GATES[binding.gateId];
  const permissions = binding.permissions ?? gatePermissionCodes(binding.gateId);
  return {
    gateId: binding.gateId,
    permissions,
    placeholder: binding.placeholder,
    requestable: gateRequestable(binding.gateId),
    titleI18nKey: row.titleI18nKey,
    accessRequestModule: row.accessRequestModule,
    placeholderModule: row.placeholderModule,
  };
}
